import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import DashboardLayout from '../components/shared/DashboardLayout';
import { useTheme } from '../context/ThemeContext';
import API from '../utils/api';
import { isNative } from '../utils/capacitor';
import { trackReadingReportViewed } from '../utils/analytics';

const GENRE_ICONS = {
  magical: '✨', adventure: '🗺️', adventurous: '🗺️', funny: '😄',
  calm: '🌙', educational: '📚', cozy: '🔥', exciting: '⚡',
  spooky: '👻', fantasy: '🧙', mystery: '🔍', nature: '🌿',
  science: '🔬', bedtime: '🛌', default: '📖',
};
const genreIcon = (g) => GENRE_ICONS[g?.toLowerCase()] || GENRE_ICONS.default;
const capitalize = (s) => s ? s.charAt(0).toUpperCase() + s.slice(1) : '—';

const StatCard = ({ icon, value, label, sub, color = '#6f42c1', bg = 'var(--purple-section)' }) => (
  <div className="card border-0 shadow-sm h-100" style={{ borderRadius: '16px' }}>
    <div className="card-body p-4 d-flex flex-column align-items-center text-center">
      <div className="rounded-circle d-flex align-items-center justify-content-center mb-3"
        style={{ width: '52px', height: '52px', background: bg, fontSize: '22px' }}>
        {icon}
      </div>
      <div className="fw-bold mb-1" style={{ fontSize: '32px', color, lineHeight: 1 }}>{value}</div>
      <div className="fw-semibold mb-0" style={{ color: 'var(--text-main)', fontSize: '13px' }}>{label}</div>
      {sub && <div className="text-muted mt-1" style={{ fontSize: '11px' }}>{sub}</div>}
    </div>
  </div>
);

const ReadingReportPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { darkMode: dm } = useTheme();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    trackReadingReportViewed(id);
    API.get(`/children/${id}/stats`)
      .then(res => setData(res.data))
      .catch(() => setError('Could not load reading report.'))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) return (
    <DashboardLayout title="Reading Report">
      <div className="text-center py-5">
        <div className="spinner-border" style={{ color: '#6f42c1', width: '3rem', height: '3rem' }}></div>
        <p className="text-muted mt-3">Loading reading report…</p>
      </div>
    </DashboardLayout>
  );

  if (error) return (
    <DashboardLayout title="Reading Report">
      <div className="alert alert-danger">{error}</div>
      <Link to="/dashboard/children" className="btn btn-outline-secondary rounded-pill">← Back to Profiles</Link>
    </DashboardLayout>
  );

  const { child, stats } = data;
  const {
    totalStories, totalReads, totalFaves, favoriteGenre, topGenres,
    mostReadStory, lastReadStory, monthlyReport, topLearningTopics,
    avgRating, ratedCount, ratingDistribution, topRatedStory,
  } = stats;

  // Chart helpers
  const maxGenerated = Math.max(...monthlyReport.map(m => m.storiesGenerated), 1);
  const maxReads     = Math.max(...monthlyReport.map(m => m.storiesRead), 1);
  const maxBar       = Math.max(maxGenerated, maxReads, 1);

  const handlePrint = () => window.print();

  return (
    <DashboardLayout title="Reading Report">
      {/* Back + actions */}
      <div className="d-flex align-items-center justify-content-between mb-4 flex-wrap gap-2">
        <Link to="/dashboard/children" className="btn btn-sm btn-outline-secondary rounded-pill">
          <i className="bi bi-arrow-left me-1"></i>Back to Profiles
        </Link>
        <div className="d-flex gap-2 flex-wrap d-print-none">
          <Link to={`/dashboard/children/${id}/wrapped`}
            className="btn btn-sm rounded-pill fw-semibold"
            style={{ background: 'linear-gradient(135deg,#2d1b69,#6f42c1)', color: 'white' }}>
            <i className="bi bi-stars me-1"></i>Year in Stories
          </Link>
          {/* Hidden in the apps: window.print() is a no-op in the Android/iOS
              WebView, so this button did nothing at all when tapped. */}
          {!isNative() && (
            <button className="btn btn-sm btn-outline-secondary rounded-pill" onClick={handlePrint}>
              <i className="bi bi-printer me-1"></i>Print / Save PDF
            </button>
          )}
        </div>
      </div>

      {/* Hero banner */}
      <div className="rounded-4 p-4 mb-4 d-flex align-items-center gap-4 flex-wrap"
        style={{ background: 'linear-gradient(135deg,#2d1b69 0%,#6f42c1 100%)' }}>
        <div className="d-flex align-items-center justify-content-center rounded-circle shadow"
          style={{ width: '72px', height: '72px', background: 'rgba(255,255,255,0.15)', fontSize: '36px', flexShrink: 0 }}>
          {child.avatar}
        </div>
        <div>
          <h3 className="fw-bold mb-1 text-white" style={{ fontFamily: "'Fredoka One',cursive" }}>
            {child.name}'s Reading Report
          </h3>
          <div className="text-white-50" style={{ fontSize: '14px' }}>
            Age {child.age} · All-time reading stats · Generated {new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
          </div>
        </div>
        {totalStories === 0 && (
          <div className="ms-auto">
            <Link to="/dashboard/generate" className="btn btn-light rounded-pill px-4 fw-semibold">
              <i className="bi bi-magic me-1"></i>Generate First Story
            </Link>
          </div>
        )}
      </div>

      {/* ── Stat Cards ── */}
      <div className="row g-3 mb-4">
        <div className="col-6 col-lg">
          <StatCard icon="📚" value={totalStories} label="Stories Generated" sub="all time" color="#6f42c1" bg="var(--purple-section)" />
        </div>
        <div className="col-6 col-lg">
          <StatCard icon="👁️" value={totalReads} label="Total Reads" sub="across all stories" color="#0891b2" bg="#e0f7fa" />
        </div>
        <div className="col-6 col-lg">
          <StatCard
            icon={genreIcon(favoriteGenre)}
            value={favoriteGenre ? capitalize(favoriteGenre) : '—'}
            label="Favourite Genre"
            sub={topGenres?.[1] ? `Runner-up: ${capitalize(topGenres[1].genre)}` : 'keep reading!'}
            color="#7c3aed"
            bg="#ede9fe"
          />
        </div>
        <div className="col-6 col-lg">
          <StatCard icon="❤️" value={totalFaves} label="Favourited Stories" sub="stories saved to favourites" color="#dc2626" bg="#fee2e2" />
        </div>
        <div className="col-6 col-lg">
          <StatCard
            icon="⭐"
            value={avgRating ? `${avgRating}/5` : '—'}
            label="Avg Story Rating"
            sub={ratedCount > 0 ? `from ${ratedCount} rated ${ratedCount === 1 ? 'story' : 'stories'}` : 'no ratings yet'}
            color="#d97706"
            bg="#fffbeb"
          />
        </div>
      </div>

      <div className="row g-4">
        {/* ── Left column ── */}
        <div className="col-lg-8">

          {/* Monthly Activity Chart */}
          <div className="card border-0 shadow-sm mb-4" style={{ borderRadius: '20px' }}>
            <div className="card-body p-4">
              <h6 className="fw-bold mb-1" style={{ color: 'var(--text-main)' }}>
                <i className="bi bi-bar-chart-fill me-2" style={{ color: '#6f42c1' }}></i>
                Monthly Activity
              </h6>
              <p className="text-muted small mb-4">Stories generated and read each month over the last 6 months.</p>

              {monthlyReport.every(m => m.storiesGenerated === 0) ? (
                <div className="text-center py-4">
                  <div style={{ fontSize: '48px', marginBottom: '12px' }}>📊</div>
                  <div className="text-muted small">No stories generated in the last 6 months yet.</div>
                  <Link to="/dashboard/generate" className="btn btn-dream rounded-pill px-4 mt-3">Generate a Story</Link>
                </div>
              ) : (
                <div>
                  {/* Bar chart */}
                  <div className="d-flex align-items-end gap-2" style={{ height: '160px', overflowX: 'auto' }}>
                    {monthlyReport.map((m, i) => (
                      <div key={i} className="d-flex flex-column align-items-center flex-grow-1" style={{ minWidth: '44px' }}>
                        {/* Generated bar */}
                        <div className="d-flex gap-1 align-items-end w-100 justify-content-center mb-1" style={{ height: '140px' }}>
                          <div title={`${m.storiesGenerated} generated`}
                            style={{
                              width: '14px',
                              height: `${Math.max(4, (m.storiesGenerated / maxBar) * 130)}px`,
                              background: 'linear-gradient(180deg,#9b6fe8,#6f42c1)',
                              borderRadius: '4px 4px 0 0',
                              transition: 'height 0.3s',
                              cursor: 'default',
                            }}
                          ></div>
                          <div title={`${m.storiesRead} read`}
                            style={{
                              width: '14px',
                              height: `${Math.max(m.storiesRead > 0 ? 4 : 0, (m.storiesRead / maxBar) * 130)}px`,
                              background: 'linear-gradient(180deg,#67e8f9,#0891b2)',
                              borderRadius: '4px 4px 0 0',
                              transition: 'height 0.3s',
                              cursor: 'default',
                            }}
                          ></div>
                        </div>
                        <div style={{ fontSize: '10px', color: 'var(--text-soft)', fontWeight: 600 }}>{m.shortLabel}</div>
                        <div style={{ fontSize: '9px', color: 'var(--text-soft)' }}>{m.year !== new Date().getFullYear() ? m.year : ''}</div>
                      </div>
                    ))}
                  </div>

                  {/* Legend */}
                  <div className="d-flex gap-4 mt-3 flex-wrap">
                    <div className="d-flex align-items-center gap-2">
                      <div style={{ width: '12px', height: '12px', borderRadius: '3px', background: 'linear-gradient(180deg,#9b6fe8,#6f42c1)' }}></div>
                      <span className="small text-muted">Generated</span>
                    </div>
                    <div className="d-flex align-items-center gap-2">
                      <div style={{ width: '12px', height: '12px', borderRadius: '3px', background: 'linear-gradient(180deg,#67e8f9,#0891b2)' }}></div>
                      <span className="small text-muted">Read (new reads per month)</span>
                    </div>
                  </div>

                  {/* Monthly table summary */}
                  <div className="mt-4">
                    <div className="table-responsive">
                      <table className="table table-sm mb-0">
                        <thead>
                          <tr style={{ fontSize: '11px', color: 'var(--text-soft)' }}>
                            <th style={{ fontWeight: 600, border: 'none', paddingLeft: 0 }}>Month</th>
                            <th style={{ fontWeight: 600, border: 'none' }}>Generated</th>
                            <th style={{ fontWeight: 600, border: 'none' }}>Read</th>
                            <th style={{ fontWeight: 600, border: 'none' }}>Total Reads</th>
                          </tr>
                        </thead>
                        <tbody>
                          {[...monthlyReport].reverse().map((m, i) => (
                            <tr key={i} style={{ fontSize: '12px' }}>
                              <td style={{ border: 'none', borderBottom: '1px solid var(--row-sep)', paddingLeft: 0, color: 'var(--text-muted2)', fontWeight: 500 }}>
                                {m.label}
                              </td>
                              <td style={{ border: 'none', borderBottom: '1px solid var(--row-sep)', color: '#6f42c1', fontWeight: 600 }}>
                                {m.storiesGenerated || '—'}
                              </td>
                              <td style={{ border: 'none', borderBottom: '1px solid var(--row-sep)', color: '#0891b2', fontWeight: 600 }}>
                                {m.storiesRead || '—'}
                              </td>
                              <td style={{ border: 'none', borderBottom: '1px solid var(--row-sep)', color: 'var(--text-soft)' }}>
                                {m.totalReadCount || '—'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Learning Goals — show goals vs. progress if goals are set */}
          {child.learningGoals?.length > 0 && (
            <div className="card border-0 shadow-sm mb-4" style={{ borderRadius: '20px', overflow: 'hidden' }}>
              <div style={{ height: '4px', background: 'linear-gradient(90deg,#059669,#34d399)' }}></div>
              <div className="card-body p-4">
                <div className="d-flex align-items-center justify-content-between mb-1">
                  <h6 className="fw-bold mb-0" style={{ color: 'var(--text-main)' }}>
                    <i className="bi bi-bullseye me-2" style={{ color: '#059669' }}></i>
                    This Month's Learning Goals
                  </h6>
                  <Link to="/dashboard/children" className="btn btn-sm rounded-pill px-3"
                    style={{ background: '#dcfce7', color: '#15803d', fontSize: '11px', fontWeight: 600 }}>
                    <i className="bi bi-pencil me-1"></i>Edit Goals
                  </Link>
                </div>
                <p className="text-muted small mb-4">
                  Topics {child.name}'s parent has set as learning targets this month.
                </p>
                <div className="d-flex flex-column gap-3">
                  {child.learningGoals.map((goal, i) => {
                    const covered = topLearningTopics?.find(
                      t => t.topic?.toLowerCase() === goal.toLowerCase()
                    );
                    const count = covered?.count || 0;
                    const done = count > 0;
                    return (
                      <div key={i} className="d-flex align-items-center gap-3 p-3 rounded-3"
                        style={{ background: done ? '#f0fdf4' : '#fafafa', border: `1px solid ${done ? '#bbf7d0' : '#e5e7eb'}` }}>
                        <div className="d-flex align-items-center justify-content-center flex-shrink-0"
                          style={{ width: '36px', height: '36px', borderRadius: '50%',
                            background: done ? '#dcfce7' : '#f3f4f6', fontSize: '18px' }}>
                          {done ? '✅' : '📚'}
                        </div>
                        <div className="flex-grow-1">
                          <div className="fw-semibold mb-1" style={{ color: done ? '#15803d' : '#374151', fontSize: '14px' }}>
                            {goal}
                          </div>
                          {done ? (
                            <div className="small" style={{ color: '#16a34a' }}>
                              {count} {count === 1 ? 'story' : 'stories'} covered this topic ✓
                            </div>
                          ) : (
                            <div className="small text-muted">
                              Not covered yet — generate a story with this learning topic!
                            </div>
                          )}
                        </div>
                        {!done && (
                          <Link to={`/dashboard/generate`}
                            className="btn btn-sm rounded-pill flex-shrink-0"
                            style={{ background: '#6f42c1', color: '#fff', fontSize: '11px', fontWeight: 600, padding: '4px 12px' }}>
                            <i className="bi bi-magic me-1"></i>Start
                          </Link>
                        )}
                      </div>
                    );
                  })}
                </div>
                <div className="mt-3 p-2 rounded-2 text-center" style={{ background: dm ? 'rgba(5,150,105,0.15)' : '#f0fdf4', border: '1px solid #bbf7d0' }}>
                  <span className="small fw-semibold" style={{ color: '#059669' }}>
                    {child.learningGoals.filter(goal =>
                      topLearningTopics?.some(t => t.topic?.toLowerCase() === goal.toLowerCase())
                    ).length} of {child.learningGoals.length} goals covered this month
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Top Learning Topics Explored — all-time history */}
          {topLearningTopics?.length > 0 && (
            <div className="card border-0 shadow-sm" style={{ borderRadius: '20px' }}>
              <div className="card-body p-4">
                <h6 className="fw-bold mb-1" style={{ color: 'var(--text-main)' }}>
                  <i className="bi bi-mortarboard-fill me-2" style={{ color: '#059669' }}></i>
                  Learning Topics Explored
                </h6>
                <p className="text-muted small mb-4">All real-world topics woven into {child.name}'s stories over time.</p>
                <div className="d-flex flex-column gap-2">
                  {topLearningTopics.map((t, i) => {
                    const pct = Math.round((t.count / topLearningTopics[0].count) * 100);
                    return (
                      <div key={i}>
                        <div className="d-flex justify-content-between mb-1">
                          <span className="small fw-semibold" style={{ color: '#15803d' }}>🎓 {t.topic}</span>
                          <span className="small text-muted">{t.count} {t.count === 1 ? 'story' : 'stories'}</span>
                        </div>
                        <div className="progress" style={{ height: '6px', borderRadius: '4px', background: dm ? 'rgba(5,150,105,0.2)' : '#dcfce7' }}>
                          <div className="progress-bar" style={{ width: `${pct}%`, background: '#059669', borderRadius: '4px' }}></div>
                        </div>
                      </div>
                    );
                  })}
                </div>
                {!child.learningGoals?.length && (
                  <div className="mt-3 p-3 rounded-3 d-flex align-items-center gap-2"
                    style={{ background: dm ? 'rgba(5,150,105,0.15)' : '#f0fdf4', border: '1px dashed #86efac' }}>
                    <i className="bi bi-lightbulb-fill flex-shrink-0" style={{ color: '#16a34a' }}></i>
                    <div className="small" style={{ color: '#15803d' }}>
                      <strong>Set learning goals</strong> to track which topics you want {child.name} to cover each month.{' '}
                      <Link to="/dashboard/children" style={{ color: '#059669', fontWeight: 600 }}>
                        Set goals →
                      </Link>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* ── Right column ── */}
        <div className="col-lg-4">

          {/* Most-read story */}
          <div className="card border-0 shadow-sm mb-4" style={{ borderRadius: '20px', overflow: 'hidden' }}>
            <div style={{ height: '4px', background: 'linear-gradient(90deg,#f59e0b,#fbbf24)' }}></div>
            <div className="card-body p-4">
              <h6 className="fw-bold mb-3" style={{ color: 'var(--text-main)' }}>
                <i className="bi bi-trophy-fill me-2" style={{ color: '#f59e0b' }}></i>
                Most-Read Story
              </h6>
              {mostReadStory ? (
                <Link to={`/dashboard/story/${mostReadStory._id}`} className="text-decoration-none">
                  <div className="rounded-3 p-3" style={{ background: dm ? 'rgba(217,119,6,0.15)' : '#fffbeb', border: '1px solid #fde68a', cursor: 'pointer' }}>
                    {mostReadStory.illustrationUrl && (
                      <img src={mostReadStory.illustrationUrl} alt={mostReadStory.title}
                        style={{ width: '100%', height: '100px', objectFit: 'cover', borderRadius: '8px', marginBottom: '10px' }} />
                    )}
                    <div className="fw-bold mb-1" style={{ color: 'var(--text-main)', fontSize: '14px', lineHeight: '1.3' }}>{mostReadStory.title}</div>
                    <div className="d-flex align-items-center gap-1">
                      <i className="bi bi-eye-fill" style={{ color: '#f59e0b', fontSize: '12px' }}></i>
                      <span className="small fw-semibold" style={{ color: '#92400e' }}>{mostReadStory.readCount} reads</span>
                    </div>
                    {mostReadStory.tags?.length > 0 && (
                      <div className="d-flex flex-wrap gap-1 mt-2">
                        {mostReadStory.tags.slice(0, 3).map(tag => (
                          <span key={tag} className="badge rounded-pill" style={{ background: '#fde68a', color: '#92400e', fontSize: '10px' }}>{tag}</span>
                        ))}
                      </div>
                    )}
                  </div>
                </Link>
              ) : (
                <div className="text-center py-3 text-muted small">
                  No stories read yet.<br />
                  <Link to="/dashboard/generate" style={{ color: '#6f42c1' }}>Generate one!</Link>
                </div>
              )}
            </div>
          </div>

          {/* Last read */}
          {lastReadStory && (
            <div className="card border-0 shadow-sm mb-4" style={{ borderRadius: '20px', overflow: 'hidden' }}>
              <div style={{ height: '4px', background: 'linear-gradient(90deg,#6f42c1,#9b6fe8)' }}></div>
              <div className="card-body p-4">
                <h6 className="fw-bold mb-3" style={{ color: 'var(--text-main)' }}>
                  <i className="bi bi-clock-history me-2" style={{ color: '#6f42c1' }}></i>
                  Last Read
                </h6>
                <Link to={`/dashboard/story/${lastReadStory._id}`} className="text-decoration-none">
                  <div className="p-3 rounded-3" style={{ background: 'var(--header-bg)', border: '1px solid #e8d5ff' }}>
                    <div className="fw-bold mb-1" style={{ color: 'var(--text-main)', fontSize: '14px' }}>{lastReadStory.title}</div>
                    <div className="small text-muted">
                      <i className="bi bi-calendar3 me-1"></i>
                      {new Date(lastReadStory.lastReadAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                    </div>
                  </div>
                </Link>
              </div>
            </div>
          )}

          {/* Top-rated story */}
          {topRatedStory && (
            <div className="card border-0 shadow-sm mb-4" style={{ borderRadius: '20px', overflow: 'hidden' }}>
              <div style={{ height: '4px', background: 'linear-gradient(90deg,#f59e0b,#fcd34d)' }}></div>
              <div className="card-body p-4">
                <h6 className="fw-bold mb-3" style={{ color: 'var(--text-main)' }}>
                  <i className="bi bi-star-fill me-2" style={{ color: '#f59e0b' }}></i>
                  Top-Rated Story
                </h6>
                <Link to={`/dashboard/story/${topRatedStory._id}`} className="text-decoration-none">
                  <div className="rounded-3 p-3" style={{ background: dm ? 'rgba(217,119,6,0.12)' : '#fffbeb', border: '1px solid #fde68a' }}>
                    {topRatedStory.illustrationUrl && (
                      <img src={topRatedStory.illustrationUrl} alt={topRatedStory.title}
                        style={{ width: '100%', height: '80px', objectFit: 'cover', borderRadius: '8px', marginBottom: '10px' }} />
                    )}
                    <div className="fw-bold mb-1" style={{ color: 'var(--text-main)', fontSize: '14px', lineHeight: '1.3' }}>{topRatedStory.title}</div>
                    <div className="d-flex align-items-center gap-1 mt-1">
                      {[1,2,3,4,5].map(s => (
                        <span key={s} style={{ fontSize: '14px', opacity: s <= topRatedStory.feedback.rating ? 1 : 0.25 }}>⭐</span>
                      ))}
                      <span className="small fw-semibold ms-1" style={{ color: '#92400e' }}>{topRatedStory.feedback.rating}/5</span>
                    </div>
                  </div>
                </Link>
              </div>
            </div>
          )}

          {/* Rating distribution */}
          {ratedCount > 0 && (
            <div className="card border-0 shadow-sm mb-4" style={{ borderRadius: '20px' }}>
              <div className="card-body p-4">
                <h6 className="fw-bold mb-1" style={{ color: 'var(--text-main)' }}>
                  <i className="bi bi-bar-chart-fill me-2" style={{ color: '#d97706' }}></i>
                  Rating Breakdown
                </h6>
                <p className="text-muted small mb-3">How {child.name} has rated their stories.</p>
                <div className="d-flex flex-column gap-2">
                  {[5,4,3,2,1].map(star => {
                    const row = ratingDistribution?.find(r => r.star === star);
                    const count = row?.count || 0;
                    const pct = ratedCount > 0 ? Math.round((count / ratedCount) * 100) : 0;
                    return (
                      <div key={star} className="d-flex align-items-center gap-2">
                        <span style={{ fontSize: '13px', minWidth: '16px', textAlign: 'right' }}>{star}</span>
                        <span style={{ fontSize: '12px' }}>⭐</span>
                        <div className="flex-grow-1 progress" style={{ height: '8px', borderRadius: '4px', background: dm ? 'rgba(217,119,6,0.15)' : '#fef3c7' }}>
                          <div className="progress-bar" style={{ width: `${pct}%`, background: '#f59e0b', borderRadius: '4px' }}></div>
                        </div>
                        <span className="small text-muted" style={{ minWidth: '28px', textAlign: 'right' }}>{count}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {/* Genre breakdown */}
          {topGenres?.length > 0 && (
            <div className="card border-0 shadow-sm mb-4" style={{ borderRadius: '20px' }}>
              <div className="card-body p-4">
                <h6 className="fw-bold mb-3" style={{ color: 'var(--text-main)' }}>
                  <i className="bi bi-pie-chart-fill me-2" style={{ color: '#9b6fe8' }}></i>
                  Genre Breakdown
                </h6>
                <div className="d-flex flex-column gap-2">
                  {topGenres.map((g, i) => {
                    const total = topGenres.reduce((s, x) => s + x.count, 0);
                    const pct = Math.round((g.count / total) * 100);
                    return (
                      <div key={i}>
                        <div className="d-flex justify-content-between mb-1">
                          <span className="small fw-semibold" style={{ color: 'var(--text-muted2)' }}>
                            {genreIcon(g.genre)} {capitalize(g.genre)}
                          </span>
                          <span className="small text-muted">{g.count} ({pct}%)</span>
                        </div>
                        <div className="progress" style={{ height: '6px', borderRadius: '4px', background: 'var(--purple-section)' }}>
                          <div className="progress-bar"
                            style={{ width: `${pct}%`, background: i === 0 ? '#6f42c1' : i === 1 ? '#9b6fe8' : '#c4a8ff', borderRadius: '4px' }}></div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {/* Quick links */}
          <div className="card border-0 shadow-sm" style={{ borderRadius: '20px' }}>
            <div className="card-body p-4">
              <h6 className="fw-bold mb-3" style={{ color: 'var(--text-main)' }}>Quick Actions</h6>
              <div className="d-flex flex-column gap-2">
                <Link to={`/dashboard/library?childId=${id}`} className="btn btn-sm btn-outline-secondary rounded-pill text-start px-3">
                  <i className="bi bi-book me-2"></i>View {child.name}'s Library
                </Link>
                <Link to="/dashboard/generate" className="btn btn-sm btn-dream rounded-pill text-start px-3">
                  <i className="bi bi-magic me-2"></i>Generate New Story
                </Link>
                <Link to="/dashboard/children" className="btn btn-sm btn-outline-secondary rounded-pill text-start px-3">
                  <i className="bi bi-pencil me-2"></i>Edit Profile
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Print styles */}
      <style>{`
        @media print {
          .d-print-none { display: none !important; }
          .sidebar, .d-lg-flex, nav { display: none !important; }
          body { background: white !important; }
          .card { box-shadow: none !important; border: 1px solid #dee2e6 !important; }
        }
      `}</style>
    </DashboardLayout>
  );
};

export default ReadingReportPage;
